import { isOk, type Result } from '@synqo/frontend-component-library';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

/** The query param naming the record a list opens in its panel, as a notification or a mail links it. */
const OPEN_QUERY_PARAM = 'open';

/**
 * Opens the record named by the `open` query param. The param is dropped right away so a reload or a
 * back-navigation does not open it again; a record that cannot be read leaves the plain list behind.
 */
// @ai-generated 2026-10-01T12:50:47+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function useOpenFromQuery<T>(load: (id: string) => Promise<Result<T>>, open: (record: T) => void) {
    const [searchParams, setSearchParams] = useSearchParams();
    const lastOpened = useRef<string | null>(null);
    const id = searchParams.get(OPEN_QUERY_PARAM);

    useEffect(() => {
        if (id === null) {
            lastOpened.current = null;
            return;
        }
        if (lastOpened.current === id) {
            return;
        }
        lastOpened.current = id;

        const remaining = new URLSearchParams(searchParams);
        remaining.delete(OPEN_QUERY_PARAM);
        setSearchParams(remaining, { replace: true });

        void (async () => {
            try {
                const result = await load(id);
                if (isOk(result)) {
                    open(result.data);
                }
            } catch {
                return;
            }
        })();
    }, [id, searchParams, setSearchParams, load, open]);
}
